import ___svg_createlinkSvg from '../svg/createlink.svg';
import { logUsage } from 'owa-analytics';
import { DialogResponse } from 'owa-confirm-dialog';
import { differenceInMilliseconds, OwaDate, owaDate } from 'owa-datetime';
import type { EditorViewState } from 'owa-editor';
import { InsertLinkProperties, lazyDisplayInsertLinkDialog } from 'owa-editor-ribbon-secondary-ui';
import { callEditorApi } from 'owa-editor/lib/utils/callEditorApi';
import { callFormatApi } from 'owa-editor/lib/utils/callFormatApi';
import getSelectedImage from 'owa-editor/lib/utils/getSelectedImage';
import updateContentToViewState from 'owa-editor/lib/utils/updateContentToViewState';
import { getSelectedSuggestion } from 'owa-file-suggestions-search';
import loc from 'owa-localize';
import { lazyIsSharePointLink } from 'owa-sharepoint-link-detector';
import { QueryScope } from 'roosterjs-editor-types';
import { InsertLinkSource } from '../../types/InsertLinkSource';
import { getText } from '../../utils/getText';
import { createLink as createLink_1 } from './insertLink.locstring.json';
import type { RibbonButton } from './RibbonButton';
import { isBrowserSafari } from 'owa-user-agent';

export const INSERT_LINK_SHORTCUT = 'ctrl+K';
const insertLink: RibbonButton = {
    key: 'insertLink',
    title: () => loc(createLink_1),
    imageSrc: ___svg_createlinkSvg,
    // The actual keyboard shortcut handling logic is in RibbonPlugin.
    // These strings, in most cases, are documenting behaviour that already exists in
    // Rooster or is implemented by a plugin.
    shortcut: INSERT_LINK_SHORTCUT,
    onExecute: (editorViewState, formatState, targetWindow) =>
        showInsertLinkDialog(
            editorViewState,
            targetWindow,
            InsertLinkSource.OriginalRibbon,
            owaDate()
        ),
};

export default insertLink;

export async function showInsertLinkDialog(
    editorViewState: EditorViewState,
    targetWindow: Window,
    source: InsertLinkSource,
    startTime: OwaDate,
    onDismissDialog?: () => void
) {
    logUsage('showInsertLinkDialog', { source: source });

    const range = callEditorApi(editorViewState, 'getSelectionRange');
    const insertLink = await createInsertLink(editorViewState, range);
    const displayInsertLinkDialog = await lazyDisplayInsertLinkDialog.import();
    const dialogResult = await displayInsertLinkDialog(insertLink, targetWindow);

    if (isBrowserSafari()) {
        // Safari can still lose selection when trigger the dialog with CMD+K, so restore the selection here
        callEditorApi(editorViewState, 'focus');
        callEditorApi(editorViewState, 'select', range as any);
    }

    if (onDismissDialog) {
        onDismissDialog();
    }
    if (getSelectedSuggestion() && insertLink.FromSearch) {
        const suggestion = getSelectedSuggestion();
        insertLink.HasChanged = true;
        insertLink.LinkUrl = suggestion.FileUrl;
        insertLink.DisplayText = suggestion.FileName;
    }

    if (DialogResponse.ok === dialogResult && insertLink.HasChanged) {
        const isSharePointLink = await lazyIsSharePointLink.import();
        if (!isSharePointLink(insertLink.LinkUrl)) {
            logUsage(
                'HyperLinkNonODBLink',
                { source: source },
                {
                    cosmosOnlyData: JSON.stringify({
                        timeSpan: differenceInMilliseconds(owaDate(), startTime),
                    }),
                }
            );
        }

        updateContentToViewState(editorViewState);

        callFormatApi(
            editorViewState,
            'createLink',
            [
                insertLink.LinkUrl,
                insertLink.LinkUrl,
                insertLink.DisplayText.length > 0 ? insertLink.DisplayText : null,
            ],
            [] /* pass empty array to avoid logging */
        );
    }
}

async function createInsertLink(
    editorViewState: EditorViewState,
    range: Range
): Promise<InsertLinkProperties> {
    let insertLink: InsertLinkProperties = {
        LinkUrl: '',
        DisplayText: '',
        HasChanged: false,
        FromSearch: false,
    };

    // Check if there is an image within the selection
    const imgNode = getSelectedImage(editorViewState, true /*includeEditorSelection*/);
    if (imgNode) {
        if (!range.intersectsNode(imgNode)) {
            // If an image is selected but not within the selection range,
            // add it to the range
            range.selectNode(imgNode);
        }
        const rangeContent = range.cloneContents();
        if (insertLink.SelectionContent !== rangeContent) {
            insertLink.HasChanged = true;
        }
        insertLink.SelectionContent = rangeContent;
    }

    // Check if there is an existing hyperlink within the selection
    // If so, the default text of "Web address" input should match the existing link
    const linkNode: HTMLAnchorElement | null = getLinkNodeInsideSelection(editorViewState, range);
    if (!!linkNode) {
        insertLink.LinkUrl = linkNode.href;

        // Check if it is a beautiful link
        const isSharePointLink = await lazyIsSharePointLink.import()!;
        if (isSharePointLink(insertLink.LinkUrl)) {
            insertLink.DisplayText = getText(linkNode);
            insertLink.SelectionContent = null;
            insertLink.HasChanged = false;
            return insertLink;
        }
    }

    if (!insertLink.SelectionContent) {
        // If no image is selected, set DisplayText to put a default value in the "Display as" input
        // If the selection is within a link element, the default text should be the text of the link element
        // Otherwise, the default text should be empty
        insertLink.DisplayText = linkNode?.textContent || range?.toString() || '';
    }

    return insertLink;
}

export function getLinkNodeInsideSelection(
    editorViewState: EditorViewState,
    range: Range
): HTMLAnchorElement | null {
    // Check if there is an existing hyperlink within the selection
    const linkNodes = callEditorApi(
        editorViewState,
        'queryElements',
        'a[href]',
        QueryScope.OnSelection
    );
    const linkNode = linkNodes?.[0] as HTMLAnchorElement;
    if (linkNode && range?.intersectsNode(linkNode)) {
        return linkNode;
    }

    return null;
}
